import { getArgumentsFromStoreFieldName } from "@/cache/Utils";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client/react";
import {
  BidDecisionFragmentDoc,
  SubmitDecisionMutation,
  SubmitDecisionMutationVariables,
  SubmitDecisionDocument,
  BidDecisionGroupPartialFragmentDoc,
} from "@/graphql/__generated__/graphql";
import { Reference, StoreObject } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CustomToastComponent } from "@/components/shared";

/**
 * Custom hook for submitting a tender decision and handling the necessary cache updates.
 * @returns {Object} - Contains the submitDecisionMutation function, loading state, and error state.
 */
export default function useSubmitDecisionMutationHook(): {
  submitDecisionMutation: any;
  loading: any;
  error: any;
  } {

  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{tenderID: string}>();

  const [submitDecisionMutation, { loading, error }] = useMutation<SubmitDecisionMutation, SubmitDecisionMutationVariables>(
    SubmitDecisionDocument, {
      onCompleted: () => {
        toast(CustomToastComponent, {
          toastId: "bid-decision-submit-success",
          data: {
            title: t("toasts.biddecisionsuccess"),
            type: "success",
          },
        });
      },
      update: (cache, { data }) => {
        const newBidDecision = data?.submitDecision.bidDecision;
        const newBidDecisionGroup = data?.submitDecision.bidDecisionGroup;
        const newTenderState = data?.submitDecision.tenderState;

        // Update the BidDecisionGroup with the new fields
        if (newBidDecisionGroup?.id != null && newBidDecisionGroup?.autoApproved != null) {
          cache.writeFragment({
            id: cache.identify({ __typename: "BidDecisionGroup", id: newBidDecisionGroup.id }),
            fragment: BidDecisionGroupPartialFragmentDoc,
            data: {
              __typename: "BidDecisionGroup",
              id: newBidDecisionGroup.id,
              autoApproved: newBidDecisionGroup.autoApproved,
              awaitingOrganizationId: newBidDecisionGroup.awaitingOrganizationId ?? null,
              currentFlipOrganizationId: newBidDecisionGroup.currentFlipOrganizationId ?? null,
              currentDecision: newBidDecisionGroup.currentDecision ?? null,
            }
          });
        }

        /**
       * Update the cache for the 'fetchBidDecisionsByGroup' query.
       * We make use of the 'storeFieldName' from Apollo in order to update the correct cache field.
       ** DO NOT MODIFY THIS CODE BLOCK.
       */
        cache.modify({
          fields: {
            fetchBidDecisionsByGroup(existingData = {}, { storeFieldName, readField }) {
            // Extract arguments from 'storeFieldName'
              const args = getArgumentsFromStoreFieldName(storeFieldName);

              // If arguments are missing or don't match, return existing data
              if (!args || args.bidDecisionGroupId !== newBidDecision?.bidDecisionGroupId) {
                return existingData;
              }

              const existingBidDecisions = existingData.bidDecisions || [];

              // Check for duplicates
              if (
                existingBidDecisions.some(
                  (decisionRef: Reference | StoreObject | undefined) => readField("id", decisionRef) === newBidDecision?.id
                )
              ) {
                return existingData;
              }

              // Write the new bid decision to the cache
              const newBidDecisionRef = cache.writeFragment({
                data: newBidDecision,
                fragment: BidDecisionFragmentDoc,
              });

              // Return the updated data with the new bid decision added
              return {
                ...existingData,
                bidDecisions: [...existingBidDecisions, newBidDecisionRef],
              };
            },
          },
        });

        // Update the Tender with the new state
        cache.modify({
          id: cache.identify({ __typename: "Tender", id: tenderID }),
          fields: {
            state() {
              return newTenderState;
            },
          },
        });
      },
    });

  return {
    submitDecisionMutation,
    loading,
    error,
  };
}
